import { Fragment, useState } from 'react';
import styles from './index.module.less';
import { useRouter } from 'next/router';
import { RightOutlined } from '@ant-design/icons';
import SvgIcon from './SvgIcon';
import type { DropdownConfig, NavMenuItem, NavMenuSection, NavIconType } from '@/components/Layouts/NavItem';

interface HeaderDropdownProps {
  config: DropdownConfig;
}

const HeaderDropdown = ({ config }: HeaderDropdownProps) => {
  const router = useRouter();
  const [activeTabKey, setActiveTabKey] = useState<string>(config.tabs?.[0]?.key || '');

  const handleItemClick = (item: NavMenuItem) => {
    if (!item.path) return;
    if (item.isExternal) {
      window.open(item.path, '_blank');
    } else {
      router.push(item.path);
    }
  };

  const handleSectionTitleClick = (section: NavMenuSection) => {
    if (section.path) {
      router.push(section.path);
    }
  };

  // 渲染图标（内联渲染 SVG 以支持 currentColor）
  const renderIcon = (icon: NavIconType | undefined, className: string, size: number = 16) => {
    if (!icon) return null;
    return <SvgIcon src={icon} className={className} size={size} />;
  };

  // 渲染菜单项
  const renderMenuItem = (item: NavMenuItem, index: number) => (
    <div
      key={index}
      className={`${styles.listItem} ${item.path ? styles.clickable : ''}`}
      onClick={() => handleItemClick(item)}
    >
      {renderIcon(item.icon, styles.itemIcon)}
      <span className={styles.itemText}>{item.text}</span>
    </div>
  );

  // 渲染单个 section（items 分两列显示）
  const renderSingleSection = (section: NavMenuSection) => {
    const items = section.items;
    const midIndex = Math.ceil(items.length / 2);
    const leftItems = items.slice(0, midIndex);
    const rightItems = items.slice(midIndex);

    return (
      <>
        <div className={styles.featureColumn}>
          <div className={styles.columnHeading} onClick={() => handleSectionTitleClick(section)}>
            <span className={`${styles.columnTitle} ${section.path ? styles.clickable : ''}`}>
              {section.title}
              {section.path && <span className={styles.arrow}> →</span>}
            </span>
          </div>
          {leftItems.length > 0 && (
            <div className={styles.list}>{leftItems.map((item, itemIndex) => renderMenuItem(item, itemIndex))}</div>
          )}
        </div>
        {rightItems.length > 0 && (
          <div className={styles.featureColumn}>
            <div className={styles.columnHeadingPlaceholder} />
            <div className={styles.list}>
              {rightItems.map((item, itemIndex) => renderMenuItem(item, itemIndex + midIndex))}
            </div>
          </div>
        )}
      </>
    );
  };

  // 渲染多个 sections
  const renderMultipleSections = (sections: NavMenuSection[]) => {
    return sections.map((section, sectionIndex) => (
      <div key={sectionIndex} className={styles.featureColumn}>
        <div className={styles.columnHeading} onClick={() => handleSectionTitleClick(section)}>
          <span className={`${styles.columnTitle} ${section.path ? styles.clickable : ''}`}>
            {section.title}
            {section.path && <span className={styles.arrow}> →</span>}
          </span>
        </div>
        {section.items.length > 0 && (
          <div className={styles.list}>{section.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}</div>
        )}
      </div>
    ));
  };

  // 渲染 simple 布局 (Resources / Contact)
  const renderSimpleLayout = () => {
    const groups = config.groups || [];
    // 多分组时（Resources 的 Resources / Cooperation）列间加竖分隔线
    const isMultiGroup = groups.length > 1;
    return (
      <div className={styles.simpleContainer}>
        <div className={`${styles.simpleContent} ${isMultiGroup ? styles.multiGroup : ''}`}>
          {groups.map((group, groupIndex) => (
            <Fragment key={groupIndex}>
              {isMultiGroup && groupIndex > 0 && <div className={styles.groupDivider} />}
              <div className={styles.featureColumn}>
                {group.title && <div className={styles.groupTitle}>{group.title}</div>}
                <div className={styles.list}>
                  {group.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
                </div>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    );
  };

  // 渲染 columns 布局 (Solutions)
  const renderColumnsLayout = () => {
    const groups = config.groups || [];
    return (
      <div className={styles.columnsContainer}>
        <div className={styles.columnsContent}>
          {groups.map((group, groupIndex) => (
            <div key={groupIndex} className={styles.featureColumn}>
              <div className={styles.columnHeading}>
                <span className={styles.columnTitle}>{group.title}</span>
              </div>
              <div className={styles.list}>{group.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 渲染 tabs 布局 (Tools)
  const renderTabsLayout = () => {
    const tabs = config.tabs || [];
    const activeTab = tabs.find((tab) => tab.key === activeTabKey) || tabs[0];

    return (
      <div className={styles.tabsContainer}>
        <div className={styles.tabsContent}>
          {/* 左侧 Tab 列表 */}
          <div className={styles.tabsList}>
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`${styles.tabMenuItem} ${activeTabKey === tab.key ? styles.tabMenuItemActive : ''}`}
                onClick={() => setActiveTabKey(tab.key)}
              >
                <div className={styles.tabMenuItemContent}>
                  {renderIcon(tab.icon, styles.tabIcon, 20)}
                  <span className={styles.tabLabel}>{tab.label}</span>
                </div>
                <RightOutlined className={styles.tabArrow} />
              </div>
            ))}
          </div>

          {/* 右侧内容区域 */}
          <div className={styles.tabPanelWrapper}>
            <div className={styles.tabPanel}>
              {/* Tab 内容的 sections */}
              {activeTab?.sections.length === 1
                ? renderSingleSection(activeTab.sections[0])
                : activeTab && renderMultipleSections(activeTab.sections)}

              {/* 右侧固定 sidePanel */}
              {config.sidePanel && (
                <div className={styles.featureColumn}>
                  <div className={styles.columnHeading}>
                    <span className={styles.columnTitle}>{config.sidePanel.title}</span>
                  </div>
                  <div className={styles.list}>
                    {config.sidePanel.items.map((item, itemIndex) => renderMenuItem(item, itemIndex))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 根据 layout 类型渲染对应布局
  switch (config.layout) {
    case 'simple':
      return renderSimpleLayout();
    case 'columns':
      return renderColumnsLayout();
    case 'tabs':
      return renderTabsLayout();
    default:
      return null;
  }
};

export default HeaderDropdown;
